import type { FC, PropsWithChildren } from 'react';
import { useCallback, useEffect, useMemo } from 'react';

import { useGetFeatures } from '@/features/api/hooks/shared-feature/useGetFeatures';
import { FEATURE_VALUE_DEFAULTS } from '@/features/feature-management/constants/defaults';
import type { FeatureContextValues } from '@/features/feature-management/provider/useFeature';
import { FeatureContext } from '@/features/feature-management/provider/useFeature';
import { useFeatureSettingStore } from '@/features/feature-management/store/featureSettingStore';
import { FeaturesEnum } from '@/features/feature-management/types/features';
import type { FeatureSetting } from '@/features/feature-management/types/featureSetting';
import { StateView } from '@/shared/components/state-view/StateView';

// Todo: maybe will be a good idea to merge with app state provider so we only have one global loader
// Todo: what about local features
// Note: Removed isFetching from StateView isLoading
export const FeatureProvider: FC<PropsWithChildren> = ({ children }) => {
  const localFeatures = useFeatureSettingStore((state) => state.features);
  const setFeature = useFeatureSettingStore((state) => state.setFeature);

  const { data: remoteFeatures, isRefetching, refetch } = useGetFeatures();

  const features = useMemo(() => {
    const combinedFeatures: FeatureSetting[] = [];

    if (remoteFeatures) {
      for (const feature of remoteFeatures) {
        combinedFeatures.push({ ...feature, remoteEnabled: feature.enabled });
      }
    }

    for (const feature of localFeatures) {
      const existingFeature = combinedFeatures.find(
        (f) => f.featureIdentifier === feature.featureIdentifier
      );
      if (existingFeature) {
        existingFeature.enabled = feature.enabled;
        existingFeature.localEnabled = feature.enabled;
      } else {
        combinedFeatures.push({ ...feature, localEnabled: feature.enabled });
      }
    }

    return combinedFeatures;
  }, [localFeatures, remoteFeatures]);

  const getFeature = useCallback(
    <T extends FeatureSetting>(featureIdentifier: FeaturesEnum) => {
      const feature = features.find(
        (feature) => feature.featureIdentifier == featureIdentifier
      );

      // A feature without a backend configuration is a normal state
      // (per-tenant config, not seeded locally, disabled in dev) — fall back
      // to the defaults silently.
      const defaultFeatureValues = FEATURE_VALUE_DEFAULTS[featureIdentifier];
      if (!feature) {
        return { featureValues: defaultFeatureValues } as T;
      }

      return {
        ...feature,
        featureValues: { ...defaultFeatureValues, ...feature.featureValues }
      } as T;
    },
    [features]
  );

  const isFeatureEnabled = useCallback(
    (featureIdentifier: FeaturesEnum) => {
      if (featureIdentifier === FeaturesEnum.DevMode && !__DEV__) return false;

      return getFeature(featureIdentifier)?.enabled === true;
    },
    [getFeature]
  );

  const contextValues: FeatureContextValues = useMemo(
    () => ({
      features,
      isRefetching,
      getFeature,
      isFeatureEnabled,
      refetch
    }),
    [features, getFeature, isFeatureEnabled, isRefetching, refetch]
  );

  useEffect(() => {
    // initialize local features. Check if they exist before overriding them
    if (
      !localFeatures.some(
        (f) => f.featureIdentifier === FeaturesEnum.ExampleLocalFeature
      )
    )
      setFeature(FeaturesEnum.ExampleLocalFeature, true);

    if (
      !localFeatures.some((f) => f.featureIdentifier === FeaturesEnum.DarkMode)
    )
      setFeature(FeaturesEnum.DarkMode, false);

    if (
      !localFeatures.some((f) => f.featureIdentifier === FeaturesEnum.DevMode)
    )
      setFeature(FeaturesEnum.DevMode, false);
  }, [localFeatures, setFeature]);

  return (
    <FeatureContext.Provider value={contextValues}>
      <StateView asView>{children}</StateView>
    </FeatureContext.Provider>
  );
};
